/* ATHLO+ ADDON — THE SIGN-IN GUARD FOR APP BROWSERS.
   Remove this file and its one <script> line in layout/theme.liquid to revert. Nothing else
   depends on it existing: hub-athlete-card.js asks for window.HubInApp and shows its Google
   button when the answer is missing, which is exactly what it did before this file was written.

   WHAT IT IS FOR. Shopify's hosted sign-in answers "Invalid state parameter" when the Google
   round trip starts inside an app's own browser. The webview that Instagram, Facebook, Telegram
   or TikTok opens a link in is a cookie jar belonging to that app: Google's sign-in is handed to
   the real browser, the callback lands there too, and the cookie holding the OAuth `state` stayed
   behind in the app. The whole exchange happens on shopify.com and accounts.google.com, so there
   is no version of this a theme can fix — only warn about, and stop walking people into.

   Reported from a live event on 2026-08-16, where the audience arrived by QR code and shared
   links. Every error logged that day was iOS 18.7.

   WHY IT IS ONE FILE AND NOT FOUR. The rule lives here and only here. `bookings` carried three
   copies of "who is calling" and two of them still had the bug the third was fixed for; a rule
   about which browser this is would rot the same way across a quiz, a modal, a locker and a
   popup. Everything else asks this file.

   WHAT IT DOES NOT DO. It never hides a sign-in ROUTE, only a Google BUTTON. The email-code
   sign-in on Shopify's page works perfectly inside these browsers, so an athlete who follows any
   of these links can still get in — they just need to be told which door to use. Taking the
   route away would be a bigger bug than the one this fixes.
*/
(function () {
  'use strict';

  // DETECTION IS DELIBERATELY ASYMMETRIC. A false positive costs a convenience — the athlete
  // types an address, and still gets their card, their locker and their points. A false negative
  // costs the whole sign-in, in front of somebody, at a booth. So this leans towards suspecting.
  //
  // The iOS rule is the broad one and it is the one that matters: a REAL browser on iOS always
  // names itself, and a webview embedded in someone else's app names none of them. Telegram is
  // why that cannot be a list of app names — its browser announces nothing at all.
  //
  // REAL_IOS_BROWSER is an EXEMPTION LIST, not a description. Chrome, Firefox and Edge on iOS are
  // WKWebViews underneath and completely capable of this round trip; anything reusing this as a
  // general "am I in a webview?" test would be wrong about all three.
  var IN_APP_NAMED = /FBAN|FBAV|FB_IAB|FBIOS|Instagram|Line\/|MicroMessenger|TikTok|BytedanceWebview|musical_ly|Snapchat|WhatsApp|LinkedInApp|Twitter|Pinterest|GSA\//i;
  var REAL_IOS_BROWSER = /Safari\/|CriOS|FxiOS|EdgiOS|DuckDuckGo|OPiOS|Chrome/i;

  // '' means "a browser that can finish the round trip". Anything else is a short reason code,
  // reported to GA4 so the cost of this mitigation is measurable rather than assumed.
  function detect() {
    var ua = '';
    try { ua = navigator.userAgent || ''; } catch (e) { return ''; }
    // NO USER AGENT IS NOT EVIDENCE. Absence must fail OPEN, or a browser that could have signed
    // in loses the button on the strength of knowing nothing about it.
    if (!ua) return '';
    if (IN_APP_NAMED.test(ua)) return 'named';        // the app says who it is
    if (/;\s*wv\)/.test(ua)) return 'android-wv';     // Android WebView marks itself
    if (/iPhone|iPad|iPod/i.test(ua) && !REAL_IOS_BROWSER.test(ua)) return 'ios-wk';
    return '';
  }

  var why = detect();

  // Published BEFORE the early return, so a caller can always ask. hub-athlete-card.js calls
  // detect() rather than reading `reason` because it renders its gate long after this runs and
  // should not care when it was decided.
  window.HubInApp = { detect: detect, reason: function () { return why; } };

  if (!why) return;

  // The hook every rule below hangs off, and the one a future surface should use rather than
  // re-testing the user agent for itself.
  try { document.documentElement.setAttribute('data-hub-inapp', why); } catch (e) {}

  // Styles inline rather than in the-hub.css, so this addon stays one file plus one loader line.
  // Only ever hides or de-emphasises; nothing here can move a layout for a normal browser,
  // because none of it applies without the attribute above.
  var CSS =
    '[data-hub-inapp] .hub-popup__google{display:none!important}' +
    // width/flex-basis are belt AND braces: the note is inserted as a SIBLING of the button row
    // precisely so it never becomes a flex item, but a future container that wraps both would put
    // it back in a line, and a squeezed note is a squeezed button row.
    '.hub-inapp-note{display:block;width:100%;flex-basis:100%;margin:.6rem 0 0;font-size:.78rem;line-height:1.45;opacity:.75;text-align:center}' +
    '.hub-inapp-note b{font-weight:600;opacity:1}';
  try {
    var st = document.createElement('style');
    st.setAttribute('data-hub-inapp-style', '');
    st.appendChild(document.createTextNode(CSS));
    (document.head || document.documentElement).appendChild(st);
  } catch (e) {}

  var NOTE_HTML = '<b>Signing in with Google won’t work inside this app.</b> ' +
                  'Use the email option on the next screen — or open athlo.sg in your browser.';

  // Only the PROMINENT sign-in controls get a note. The prose links ("sign in to sync everywhere")
  // are not where somebody decides how to sign in, and a warning stapled to every one of them
  // would be noise on a page that already has three.
  function isButton(a) {
    var c = ' ' + (a.className || '') + ' ';
    return / btn /.test(c) || /hub-popup__/.test(c) || /hubjoin__/.test(c);
  }

  // ONE note per group of buttons that sit together, not one per button: the join modal has
  // "Create my locker" and "Sign in" side by side, and two identical warnings under two adjacent
  // buttons reads as a broken template rather than as advice.
  //
  // AFTER the group, never INSIDE it. `.hubjoin__cta` is a flex ROW, so appending the note made it
  // a third column: the note got a narrow strip and both buttons were squeezed until their labels
  // clipped to "CREATE" and "SIGN I". Caught on the live page before this shipped, and it is the
  // reason the note is a sibling — a container that lays its children out in a line is the normal
  // case for a row of buttons, not an exotic one, so anything placed among them inherits that.
  //
  // Marked on the HOST rather than looked for among its children, because after that change the
  // note is not a child any more and a querySelector for it would find nothing and re-add forever.
  function annotate() {
    var links, i, a, host;
    try { links = document.querySelectorAll('a[href*="/customer_authentication/login"]'); }
    catch (e) { return 0; }
    var added = 0;
    for (i = 0; i < links.length; i++) {
      a = links[i];
      if (!isButton(a)) continue;
      host = a.parentElement;
      if (!host || host.getAttribute('data-hub-inapp-noted') != null) continue;
      host.setAttribute('data-hub-inapp-noted', '');
      var p = document.createElement('p');
      p.className = 'hub-inapp-note';
      p.innerHTML = NOTE_HTML;
      if (host.insertAdjacentElement) host.insertAdjacentElement('afterend', p);
      else host.appendChild(p);
      added++;
    }
    return added;
  }

  // The join popup's own Google button is OURS, and with it hidden the alternative below it is
  // the only way through — so it stops reading as an afterthought. The merchant's label is
  // deliberately overridden here and only here: "or use Shop or email" is a sentence about a
  // button that is no longer on screen.
  function fixJoinPopup() {
    var alt = document.getElementById('hub-popup-alt');
    if (!alt) return false;
    var span = alt.querySelector('span');
    (span || alt).textContent = 'Continue with email';
    alt.classList.add('hub-inapp-primary');
    return true;
  }

  function run() {
    annotate();
    fixJoinPopup();
    try {
      if (window.HubTrack) window.HubTrack('inapp_signin_guard', { why: why });
    } catch (e) {}
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
